/**
 * Business Improvement & Efficiency — where the operation leaks value, what
 * each leak is worth, and the initiatives that close them, ranked by payback.
 *
 * Signals come from the platform: KPIs against their targets, the overdue
 * work, the predictive precision, the unplanned closures, the energy the
 * green programme finds. Each signal is priced only where a rate exists —
 * the rate card, the finance figures, an entered value — and says which;
 * where none exists it is listed with the value left blank rather than
 * guessed. Initiatives the executive enters are ranked beside them.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { Closure } from "./closures";
import type { RateCard, SourceRef } from "./decision";
import { commercial, revenueTotal, type Finance } from "./finance";
import { t, tn } from "../i18n";
import { money } from "./intelligence";
import { findKpi, type Kpi } from "./kpis";
import type { WorkOrder } from "./workOrders";

export interface Initiative {
  key: string;
  name: string;
  area: string;
  /** the metric it moves, with where it is and where it should be */
  metric?: { label: string; now: number | null; target: number | null; unit: string };
  /** a year's value, when a rate exists */
  valuePerYear: number | null;
  valueWorking: string;
  cost: number | null;
  paybackMonths: number | null;
  owner?: string;
  status: "suggested" | "planned" | "running" | "done";
  basis: "computed" | "starting" | "entered";
  sources: SourceRef[];
}

export interface ImprovementEntered {
  initiatives?: {
    key: string;
    name: string;
    area: string;
    valuePerYear?: number;
    cost?: number;
    owner?: string;
    status?: Initiative["status"];
    metric?: string;
  }[];
  /** what a point of a KPI is worth a year, where the executive knows it */
  pointValue?: Record<string, number>;
  enteredBy?: string;
  enteredAt?: string;
}

export interface ImprovementInputs {
  now: Date;
  siteCode: string;
  kpis: Kpi[];
  workOrders: WorkOrder[];
  closures: Closure[];
  finance: Finance | null;
  rateCard: RateCard;
  entered: ImprovementEntered;
}

export interface ImprovementDesk {
  generatedAt: string;
  currency: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  initiatives: Initiative[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const DAY = 86_400_000;

export function buildImprovementDesk(inp: ImprovementInputs): ImprovementDesk {
  const cur = inp.rateCard.currency;
  const kpi = (k: string) => findKpi(inp.kpis, k);
  const kpiRef = (k: Kpi): SourceRef => ({ kind: "kpi", ref: k.key, label: k.label, href: "/executive-kpis" });
  const rate = (what: string): SourceRef => ({ kind: "rate_card", ref: "rate card", label: what });
  const f = inp.finance;
  const finRef: SourceRef | null = f
    ? f.basis === "demo"
      ? { kind: "demo", ref: "demo finance feed", label: "demonstration figures" }
      : { kind: "finance", ref: "finance", label: "finance figures" }
    : null;
  const pointValue = inp.entered.pointValue ?? {};
  const list: Initiative[] = [];
  const metricOf = (k: Kpi) => ({ label: k.label, now: k.value, target: k.target, unit: k.unit });
  const off = (k: Kpi | undefined) =>
    k && k.value != null && k.target != null && (k.direction === "down" ? k.value > k.target : k.value < k.target);

  /* 1. unplanned closures: what predictive maintenance is worth */
  const unplanned = inp.closures.filter((c) => c.kind === "unplanned" && c.minutes);
  const unplannedHours = unplanned.reduce((t, c) => t + (c.minutes ?? 0) / 60, 0);
  const precision = kpi("predictive_precision_pct");
  if (unplanned.length) {
    const days = Math.max(
      30,
      (inp.now.getTime() - Math.min(...unplanned.map((c) => new Date(c.startedAt).getTime()))) / DAY,
    );
    const perYear = (unplannedHours / days) * 365;
    const rateH = inp.rateCard.closureCostPerHour.runway;
    list.push({
      key: "predictive",
      name: t("Turn unplanned closures into planned ones"),
      area: t("Airfield"),
      metric: precision ? metricOf(precision) : undefined,
      valuePerYear: Math.round(perYear * rateH * 0.5),
      valueWorking: t(
        "{hours} h of unplanned closure in {days} days — {perYear} h a year — half of it made planned, at {rate}/h on the rate card",
        {
          hours: Math.round(unplannedHours * 10) / 10,
          days: Math.round(days),
          perYear: Math.round(perYear),
          rate: money(rateH, cur),
        },
      ),
      cost: inp.rateCard.categories.systems.unitRepairNow * 5,
      paybackMonths: null,
      status: "suggested",
      basis: "starting",
      sources: [
        ...unplanned
          .slice(0, 4)
          .map((c) => ({ kind: "closure" as const, ref: c.reference ?? c.id, label: c.reason, href: "/capacity" })),
        ...(precision ? [kpiRef(precision)] : []),
        rate("closure cost per hour, runway"),
      ],
    });
  }

  /* 2. overdue work */
  const overdue = inp.workOrders.filter(
    (w) => w.dueAt && new Date(w.dueAt) < inp.now && !["completed", "verified", "cancelled"].includes(w.status),
  );
  if (overdue.length) {
    const esc = overdue.reduce(
      (t, w) =>
        t +
        (w.costPlanned ?? inp.rateCard.categories.other.unitRepairNow) *
          inp.rateCard.categories.other.escalationPerQuarter,
      0,
    );
    list.push({
      key: "backlog",
      name: t("Clear the overdue work"),
      area: t("Maintenance"),
      metric: { label: t("work orders overdue"), now: overdue.length, target: 0, unit: t("orders") },
      valuePerYear: Math.round(esc * 4),
      valueWorking: t("{n} orders past due; each quarter late escalates the planned cost by {pct}% on the rate card", {
        n: overdue.length,
        pct: Math.round(inp.rateCard.categories.other.escalationPerQuarter * 100),
      }),
      cost: 0,
      paybackMonths: 0,
      status: "suggested",
      basis: "computed",
      sources: [
        ...overdue
          .slice(0, 5)
          .map((w) => ({ kind: "work_order" as const, ref: w.number, label: w.title, href: `/work-orders/${w.id}` })),
        rate("escalation per quarter"),
      ],
    });
  }

  /* 3. KPIs off target, each priced only where a point value is known */
  for (const [key, area, name] of [
    ["mttr_p1_hours", t("Engineering"), t("Faster priority-one repair")],
    ["outages", t("Engineering"), t("Equipment availability program")],
    ["queue", t("Terminal"), t("Passenger flow at the worst queue")],
    ["baggage", t("Terminal"), t("Bags on time")],
    ["punctuality", t("Operations"), t("On-time performance with the airlines")],
  ] as const) {
    const k = kpi(key);
    if (!off(k)) continue;
    const gap = Math.abs((k!.value ?? 0) - (k!.target ?? 0));
    const pv = pointValue[key];
    list.push({
      key: `kpi:${key}`,
      name,
      area,
      metric: metricOf(k!),
      valuePerYear: pv != null ? Math.round(gap * pv) : null,
      valueWorking:
        pv != null
          ? t("{gap} {unit} off target × {value} a point a year, entered", {
              gap,
              unit: k!.unit,
              value: money(pv, cur),
            })
          : t("{gap} {unit} off target; the value of a point is not entered, so it is not priced", {
              gap,
              unit: k!.unit,
            }),
      cost: null,
      paybackMonths: null,
      status: "suggested",
      basis: pv != null ? "entered" : "computed",
      sources: [
        kpiRef(k!),
        ...(pv != null
          ? [
              {
                kind: "entered" as const,
                ref: "entered",
                label: `value of a point of ${k!.label}`,
                enteredBy: inp.entered.enteredBy,
              },
            ]
          : []),
      ],
    });
  }

  /* 4. commercial yield */
  if (f && f.passengers) {
    const com = commercial(f);
    const gap = com.reduce((t, l) => t + Math.max(0, l.budget - l.actual), 0);
    if (gap > 0)
      list.push({
        key: "commercial",
        name: t("Commercial revenue to plan"),
        area: t("Commercial"),
        metric: {
          label: t("non-aeronautical revenue a passenger"),
          now: Math.round(com.reduce((t, l) => t + l.actual, 0) / f.passengers),
          target: Math.round(com.reduce((t, l) => t + l.budget, 0) / f.passengers),
          unit: cur,
        },
        valuePerYear: Math.round((gap / f.daysElapsed) * 365),
        valueWorking: t("{amount} under plan across {lines} in {days} days, annualised", {
          amount: money(gap, cur),
          lines: com
            .filter((l) => l.actual < l.budget)
            .map((l) => l.label.toLowerCase())
            .join(", "),
          days: f.daysElapsed,
        }),
        cost: null,
        paybackMonths: null,
        status: "suggested",
        basis: f.basis === "demo" ? "starting" : "computed",
        sources: finRef ? [finRef] : [],
      });
  }

  /* 5. the executive's own initiatives */
  for (const e of inp.entered.initiatives ?? []) {
    list.push({
      key: `own:${e.key}`,
      name: e.name,
      area: e.area,
      valuePerYear: e.valuePerYear ?? null,
      valueWorking: e.valuePerYear != null ? t("entered") : t("no value entered"),
      cost: e.cost ?? null,
      paybackMonths: e.valuePerYear && e.cost != null ? Math.round((12 * e.cost) / e.valuePerYear) : null,
      owner: e.owner,
      status: e.status ?? "planned",
      basis: "entered",
      sources: [{ kind: "entered", ref: "entered", label: e.name, enteredBy: inp.entered.enteredBy }],
    });
  }
  for (const i of list)
    if (i.paybackMonths == null && i.valuePerYear && i.cost != null)
      i.paybackMonths = Math.round((12 * i.cost) / i.valuePerYear);
  list.sort(
    (a, b) => (a.paybackMonths ?? 999) - (b.paybackMonths ?? 999) || (b.valuePerYear ?? -1) - (a.valuePerYear ?? -1),
  );

  const revPax = f?.passengers ? Math.round(revenueTotal(f) / f.passengers) : null;
  const costPax = f?.passengers ? Math.round(f.opex.actual / f.passengers) : null;
  const mttr = kpi("mttr_p1_hours");
  const tiles: ImprovementDesk["tiles"] = [
    {
      key: "costPax",
      label: t("operating cost a passenger"),
      value: costPax,
      unit: cur,
      text: f?.passengers
        ? t("{n} passengers, {period}", { n: f.passengers.toLocaleString("en"), period: f.period })
        : t("no passenger count"),
      assumed: f?.basis === "demo",
      sources: finRef ? [finRef] : [],
    },
    {
      key: "revPax",
      label: t("revenue a passenger"),
      value: revPax,
      unit: cur,
      text:
        revPax != null && costPax != null
          ? t("{amount} a passenger net", { amount: money(revPax - costPax, cur) })
          : t("needs the finance figures"),
      assumed: f?.basis === "demo",
      sources: finRef ? [finRef] : [],
    },
    {
      key: "mttr",
      label: t("priority-one repair"),
      value: mttr?.value ?? null,
      unit: mttr?.unit ?? "h",
      text: mttr ? t("target {target} {unit}", { target: mttr.target ?? "—", unit: mttr.unit }) : t("no KPI"),
      tone: off(mttr) ? "bad" : "good",
      sources: mttr ? [kpiRef(mttr)] : [],
    },
    {
      key: "precision",
      label: t("predictive precision"),
      value: precision?.value ?? null,
      unit: "%",
      text: precision
        ? t("target {target}% — how often a prediction was right", { target: precision.target ?? "—" })
        : t("no KPI"),
      tone: off(precision) ? "warn" : "good",
      sources: precision ? [kpiRef(precision)] : [],
    },
    {
      key: "unplanned",
      label: t("unplanned closure"),
      value: Math.round(unplannedHours * 10) / 10,
      unit: "h",
      text: tn(unplanned.length, "{n} unplanned closure on record", "{n} unplanned closures on record"),
      tone: unplannedHours > 4 ? "warn" : "good",
      sources: unplanned
        .slice(0, 3)
        .map((c) => ({ kind: "closure" as const, ref: c.reference ?? c.id, label: c.reason, href: "/capacity" })),
    },
    {
      key: "overdue",
      label: t("work overdue"),
      value: overdue.length,
      unit: t("orders"),
      text: overdue.length
        ? overdue
            .slice(0, 3)
            .map((w) => w.number)
            .join(", ")
        : t("nothing past due"),
      tone: overdue.length ? "warn" : "good",
      sources: [],
    },
    {
      key: "table",
      label: t("priced initiatives a year"),
      value: list.reduce((sum, i) => sum + (i.valuePerYear ?? 0), 0),
      unit: cur,
      text: t("{priced} of {n} initiatives priced", {
        priced: list.filter((i) => i.valuePerYear != null).length,
        n: list.length,
      }),
      tone: "good",
      sources: [],
    },
  ];
  const top = list.find((i) => i.valuePerYear != null);
  const unpriced = list.filter((i) => i.valuePerYear == null);
  const boardLine = [
    costPax != null && revPax != null
      ? t("{site} earns {revenue} and spends {cost} a passenger; {n} initiatives are on the table, {priced} priced.", {
          site: inp.siteCode,
          revenue: money(revPax, cur),
          cost: money(costPax, cur),
          n: list.length,
          priced: list.filter((i) => i.valuePerYear != null).length,
        })
      : t(
          "{site} has no per-passenger figures until the finance figures are entered; {n} initiatives are on the table, {priced} priced.",
          { site: inp.siteCode, n: list.length, priced: list.filter((i) => i.valuePerYear != null).length },
        ),
    top
      ? top.paybackMonths != null
        ? t("The best-paying is {name}: {value} a year, paying back in {months} months — {working}.", {
            name: top.name.toLowerCase(),
            value: money(top.valuePerYear!, cur),
            months: top.paybackMonths,
            working: top.valueWorking,
          })
        : t("The best-paying is {name}: {value} a year — {working}.", {
            name: top.name.toLowerCase(),
            value: money(top.valuePerYear!, cur),
            working: top.valueWorking,
          })
      : t("Nothing is priced yet."),
    unpriced.length
      ? unpriced.length === 1
        ? t("{n} initiative is unpriced because the value of a KPI point is not entered: {list}.", {
            n: unpriced.length,
            list: unpriced.map((i) => i.name.toLowerCase()).join("; "),
          })
        : t("{n} initiatives are unpriced because the value of a KPI point is not entered: {list}.", {
            n: unpriced.length,
            list: unpriced.map((i) => i.name.toLowerCase()).join("; "),
          })
      : t("Every initiative carries a value."),
  ];
  const material = materialForImprovement({ tiles, initiatives: list, boardLine, currency: cur } as ImprovementDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    tiles,
    initiatives: list,
    boardLine,
    gaps: [
      ...(f ? [] : [t("no finance figures")]),
      ...(unpriced.length ? [t("the value of a KPI point is not entered for every off-target KPI")] : []),
    ],
    figures,
  };
}

export const IMPROVEMENT_MODEL_PROMPT = [
  "You are advising an airport's chief executive on efficiency and improvement from the material given and nothing else.",
  "You may explain, rank and argue; you must not state any number that is not in the material.",
  "Say when a figure rests on a rate card, the demonstration or an entered value rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForImprovement(
  d: Pick<ImprovementDesk, "tiles" | "initiatives" | "boardLine" | "currency">,
): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(
      `Tile ${t.label}: ${t.value == null ? "n/a" : Math.round(t.value * 10) / 10} ${t.unit} — ${t.text}${t.assumed ? " [demo/entered]" : ""}`,
    );
  for (const i of d.initiatives)
    L.push(
      `Initiative ${i.key} "${i.name}" (${i.area}, ${i.status}, ${i.basis}): value ${i.valuePerYear == null ? "not priced" : `${Math.round(i.valuePerYear)} ${d.currency} a year`}, cost ${i.cost == null ? "n/a" : Math.round(i.cost)}, payback ${i.paybackMonths ?? "n/a"} months.${i.metric ? ` Metric ${i.metric.label}: ${i.metric.now ?? "n/a"} against ${i.metric.target ?? "n/a"} ${i.metric.unit}.` : ""} ${i.valueWorking}.`,
    );
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerImprovement(q: string, d: ImprovementDesk): string | null {
  const t = q.toLowerCase();
  if (/best|first|payback|rank|top/.test(t))
    return (
      d.initiatives
        .map(
          (i, n) =>
            `${n + 1}. ${i.name}: ${i.valuePerYear != null ? `${money(i.valuePerYear, d.currency)} a year` : "unpriced"}${i.paybackMonths != null ? `, payback ${i.paybackMonths} months` : ""} — ${i.valueWorking}`,
        )
        .join("\n") || "Nothing on the table."
    );
  if (/unpriced|blank|value of a point|not priced/.test(t))
    return (
      d.initiatives
        .filter((i) => i.valuePerYear == null)
        .map((i) => `${i.name}: ${i.valueWorking}`)
        .join("\n") || "Every initiative is priced."
    );
  if (/per pax|per passenger|a passenger/.test(t))
    return d.tiles
      .filter((x) => ["costPax", "revPax"].includes(x.key))
      .map((x) => `${x.label}: ${x.value != null ? money(x.value, d.currency) : "—"} — ${x.text}`)
      .join("\n");
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  const i = d.initiatives.find((x) => t.includes(x.name.toLowerCase().slice(0, 12)) || t.includes(x.key));
  if (i)
    return `${i.name} (${i.area}): ${i.valuePerYear != null ? `${money(i.valuePerYear, d.currency)} a year` : "unpriced"}; ${i.valueWorking}. Cost ${i.cost != null ? money(i.cost, d.currency) : "not entered"}; ${i.status}.`;
  return null;
}
